/**
 * PrintPdfService
 * 
 * Service responsible for PDF generation, printing, downloading, and caching throughout
 * the MacSafety web application. Handles both new PDF generation and retrieval of
 * existing PDFs for viewing and printing.
 * 
 * Key Features:
 * - PDF generation from HTML content with attachments
 * - PDF printing with custom HTML wrapper
 * - PDF downloading with proper filenames
 * - Intelligent localStorage caching with expiration
 * - Cache size management and cleanup
 * - Integration with form submission workflow
 * - Custom PDF viewer with print optimization
 * 
 * Note: This service is NOT used for initial form submission PDFs, which are
 * handled automatically by the form submission API endpoint. This service is
 * specifically for user-initiated PDF operations (print/download) when viewing
 * existing submitted forms.
 * 
 * Components that utilize this service:
 * - FormViewComponent: For printing and downloading existing form PDFs
 * - EmailService: For accessing cached PDFs when resending emails
 * - Any component requiring PDF generation or viewing capabilities
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { PdfGenerationRequest, PdfGenerationResult } from '../models/print-pdf/print-pdf';

/**
 * Interface for PDF generation response data
 * Contains the generated PDF blob and filename
 */
interface PdfGenerationResponse {
  blob: Blob;        // The generated PDF as a blob
  filename: string;   // The filename for the PDF
}

/**
 * Interface for cached PDF data in localStorage
 * Stores base64 encoded blob data with metadata for cache management
 */
interface CachedPdf {
  blob: string;       // Base64 encoded blob data
  filename: string;   // Original filename
  timestamp: number;  // Cache timestamp for expiration
  size: number;       // File size in bytes for cache management
}

@Injectable({
  providedIn: 'root'
})
export class PrintPdfService {
  // Base API URL for PDF generation endpoints
  private readonly apiUrl = environment.apiBaseUrl;
  
  // Cache configuration constants
  private readonly CACHE_PREFIX = 'pdf_cache_';
  private readonly CACHE_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB total cache limit

  // Signals for reactive state management
  // Loading state with message for user feedback during PDF operations
  public pdfLoading = signal<{ isLoading: boolean; message: string }>({
    isLoading: false,
    message: ''
  });

  // PDF generation result for success/error handling
  public pdfGenerationResult = signal<PdfGenerationResult | null>(null);

  constructor(
    private _httpClient: HttpClient,
    private _toastService: ToastService,
    private _entraService: EntraService
  ) {
    // Clean expired cache on service initialization
    this.cleanExpiredCache();
  }

  /**
   * Resets the PDF loading state to its initial values
   * Clears both the loading flag and any status messages
   */
  public resetPdfLoading(): void {
    this.pdfLoading.set({ isLoading: false, message: '' });
  }

  /**
   * Generates a PDF and opens it in a print dialog
   * Uses caching to improve performance for previously generated PDFs
   * 
   * @param request - PDF generation request with HTML content and metadata
   * @param formEntryId - Unique identifier for cache management
   * @param onSuccess - Optional callback for successful PDF generation
   * @param onError - Optional callback for error handling
   */
  public async generateAndPrintPdf(
    request: PdfGenerationRequest,
    formEntryId: string,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    await this.processPdfGeneration(request, 'print', formEntryId, onSuccess, onError);
  }

  /**
   * Generates a PDF and downloads it to the user's device
   * Uses caching to improve performance for previously generated PDFs
   * 
   * @param request - PDF generation request with HTML content and metadata
   * @param formEntryId - Unique identifier for cache management
   * @param onSuccess - Optional callback for successful PDF generation
   * @param onError - Optional callback for error handling
   */
  public async generateAndDownloadPdf(
    request: PdfGenerationRequest,
    formEntryId: string,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    await this.processPdfGeneration(request, 'download', formEntryId, onSuccess, onError);
  }

  /**
   * Core PDF processing logic - handles both print and download scenarios
   * Implements intelligent caching to avoid regenerating PDFs unnecessarily
   * 
   * @param request - PDF generation request data
   * @param action - Whether to print or download the PDF
   * @param formEntryId - Unique identifier for cache management
   * @param onSuccess - Optional callback for successful operation
   * @param onError - Optional callback for error handling
   */
  private async processPdfGeneration(
    request: PdfGenerationRequest,
    action: 'print' | 'download',
    formEntryId: string,
    onSuccess?: () => void,
    onError?: () => void
  ): Promise<void> {
    this.pdfLoading.set({ isLoading: true, message: 'Generating PDF...' });

    // Check cache first for improved performance
    const cachedResult = this.getCachedPdf(formEntryId);
    
    if (cachedResult) {
      this.pdfLoading.set({ isLoading: false, message: '' });
      
      // Handle the cached PDF based on action type
      if (action === 'print') {
        this.openPdfInPrintDialog(cachedResult.blob, cachedResult.filename);
      } else {
        this.downloadPdf(cachedResult.blob, cachedResult.filename);
      }

      const successMessage = action === 'print' 
        ? 'PDF opened successfully (cached)' 
        : 'PDF downloaded successfully (cached)';
      
      this._toastService.showSuccessMessage(successMessage);
      onSuccess?.();
      return;
    }

    // Generate new PDF when no cached version exists
    this.generatePdf(request).subscribe({
      next: (result: PdfGenerationResponse) => {
        console.log('PDF generated successfully:', { 
          size: result.blob.size, 
          filename: result.filename 
        });

        this.pdfGenerationResult.set({
          success: true,
          message: 'PDF generated successfully'
        });

        // Cache the result for future use
        this.cachePdf(formEntryId, result);

        // Handle the PDF based on action type
        if (action === 'print') {
          this.openPdfInPrintDialog(result.blob, result.filename);
        } else {
          this.downloadPdf(result.blob, result.filename);
        }

        const successMessage = action === 'print' 
          ? 'PDF generated successfully' 
          : 'PDF downloaded successfully';
        
        this._toastService.showSuccessMessage(successMessage);
        onSuccess?.();
      },

      error: (error) => {
        console.error(`Error generating PDF for ${action}:`, error);
        
        // Check for authentication errors
        if (error.status === 401 || error.status === 403) {
          this._entraService.sessionRefreshRequiredError();
        } else {
          this._toastService.showErrorMessage('Failed to generate PDF');
        }
        
        this.pdfGenerationResult.set({
          success: false,
          message: 'Failed to generate PDF',
          error: error.message || 'Unknown error'
        });

        this.pdfLoading.set({ isLoading: false, message: '' });
        onError?.();
      },

      complete: () => {
        this.pdfLoading.set({ isLoading: false, message: '' });
      }
    });
  }

  /**
   * Caches PDF data in localStorage for improved performance
   * Converts blob to base64 for storage and enforces cache size limits
   * 
   * @param formEntryId - Unique identifier for the form entry
   * @param pdfData - PDF data to cache (blob and filename)
   */
  private cachePdf(formEntryId: string, pdfData: PdfGenerationResponse): void {
    try {
      // Check cache size limits before adding new entry
      this.enforceCacheSizeLimit();

      // Convert blob to base64 for localStorage storage
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result as string;
        
        const cachedPdf: CachedPdf = {
          blob: base64Data,
          filename: pdfData.filename,
          timestamp: Date.now(),
          size: pdfData.blob.size
        };

        const cacheKey = this.CACHE_PREFIX + formEntryId;
        localStorage.setItem(cacheKey, JSON.stringify(cachedPdf));
        console.log(`Cached PDF: ${formEntryId} (${pdfData.blob.size} bytes)`);
      };
      reader.readAsDataURL(pdfData.blob);
    } catch (error) {
      console.warn('Failed to cache PDF:', error);
      // Don't fail the operation if caching fails
    }
  }

  /**
   * Retrieves cached PDF data from localStorage
   * Checks expiration and converts base64 data back to blob
   * 
   * @param formEntryId - Unique identifier for the form entry
   * @returns Cached PDF data or null if not found/expired
   */
  private getCachedPdf(formEntryId: string): PdfGenerationResponse | null {
    try {
      const cacheKey = this.CACHE_PREFIX + formEntryId;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;

      const cachedPdf: CachedPdf = JSON.parse(cached);
      
      // Check if cache entry has expired
      const isExpired = (Date.now() - cachedPdf.timestamp) > this.CACHE_DURATION;
      if (isExpired) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Convert base64 back to blob for use
      const byteCharacters = atob(cachedPdf.blob.split(',')[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      return {
        blob,
        filename: cachedPdf.filename
      };
    } catch (error) {
      console.warn('Failed to retrieve cached PDF:', error);
      // Clean up corrupted cache entry
      const cacheKey = this.CACHE_PREFIX + formEntryId;
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  /**
   * Cleans expired cache entries from localStorage
   * Called during service initialization and can be called manually
   * Removes entries older than CACHE_DURATION
   */
  private cleanExpiredCache(): void {
    const now = Date.now();
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const cachedPdf: CachedPdf = JSON.parse(cached);
            const isExpired = (now - cachedPdf.timestamp) > this.CACHE_DURATION;
            if (isExpired) {
              keysToRemove.push(key);
            }
          }
        } catch {
          // Remove corrupted entries
          keysToRemove.push(key);
        }
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      console.log(`Cleaned ${keysToRemove.length} expired PDF cache entries`);
    }
  }

  /**
   * Enforces cache size limits by removing oldest entries
   * Prevents localStorage from becoming too large
   * Removes entries based on timestamp when total size exceeds MAX_CACHE_SIZE
   */
  private enforceCacheSizeLimit(): void {
    const cacheEntries: Array<{key: string, data: CachedPdf}> = [];
    let totalSize = 0;

    // Collect all cache entries with their metadata
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const data: CachedPdf = JSON.parse(cached);
            cacheEntries.push({ key, data });
            totalSize += data.size;
          }
        } catch {
          // Remove corrupted entries
          localStorage.removeItem(key);
        }
      }
    }

    // Remove oldest entries if over size limit
    if (totalSize > this.MAX_CACHE_SIZE) {
      // Sort by timestamp (oldest first) for LRU eviction
      cacheEntries.sort((a, b) => a.data.timestamp - b.data.timestamp);
      
      while (totalSize > this.MAX_CACHE_SIZE && cacheEntries.length > 0) {
        const oldest = cacheEntries.shift()!;
        localStorage.removeItem(oldest.key);
        totalSize -= oldest.data.size;
        console.log(`Removed old PDF cache entry: ${oldest.key} (${oldest.data.size} bytes)`);
      }
    }
  }

  /**
   * Opens a PDF blob in a new tab with custom HTML wrapper and print optimization
   * Creates a professional PDF viewer with filename display and print-friendly styling
   * 
   * @param pdfBlob - PDF blob data to display
   * @param fileName - Name of the file for display in header and browser tab
   */
  public openPdfInPrintDialog(pdfBlob: Blob, fileName: string): void {
    try {
      // Create a File object with the correct filename
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(file);
      
      // Create HTML wrapper to show filename in browser tab and provide print optimization
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${this.escapeHtml(fileName)}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { 
              margin: 0; 
              padding: 0; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #f5f5f5;
            }
            .header {
              background: #ffffff;
              padding: 12px 20px;
              border-bottom: 1px solid #e0e0e0;
              font-size: 14px;
              text-align: center;
              color: #333;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              position: sticky;
              top: 0;
              z-index: 100;
            }
            .filename {
              font-weight: 600;
              color: #1976d2;
            }
            iframe { 
              width: 100%; 
              height: calc(100vh - 60px); 
              border: none;
              background: white;
            }
            .loading {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              color: #666;
              font-size: 16px;
            }
            .spinner {
              border: 3px solid #f3f3f3;
              border-top: 3px solid #1976d2;
              border-radius: 50%;
              width: 30px;
              height: 30px;
              animation: spin 1s linear infinite;
              margin: 0 auto 10px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @media print {
              .header { display: none; }
              iframe { height: 100vh; }
            }
            @media (max-width: 768px) {
              .header { padding: 8px 12px; font-size: 12px; }
              iframe { height: calc(100vh - 50px); }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="filename">${this.escapeHtml(fileName)}</div>
          </div>
          <div class="loading" id="loading">
            <div class="spinner"></div>
            <div>Loading PDF...</div>
          </div>
          <iframe 
            src="${pdfUrl}" 
            type="application/pdf" 
            title="${this.escapeHtml(fileName)}"
            id="pdfFrame"
          ></iframe>
          <script>
            // Set document title for browser tab
            document.title = '${this.escapeHtml(fileName)}';
            
            // Hide loading spinner when PDF loads
            const iframe = document.getElementById('pdfFrame');
            const loading = document.getElementById('loading');
            
            iframe.addEventListener('load', function() {
              if (loading) {
                loading.style.display = 'none';
              }
            });
            
            // Fallback - hide loading after 3 seconds
            setTimeout(function() {
              if (loading) {
                loading.style.display = 'none';
              }
            }, 3000);
            
            // Clean up blob URLs when window closes
            window.addEventListener('beforeunload', function() {
              URL.revokeObjectURL('${pdfUrl}');
            });
            
            // Auto-focus for better user experience
            window.addEventListener('load', function() {
              setTimeout(function() {
                window.focus();
              }, 100);
            });

            // Handle PDF load errors
            iframe.addEventListener('error', function() {
              document.body.innerHTML = \`
                <div style="text-align: center; padding: 50px; font-family: Arial, sans-serif;">
                  <h2 style="color: #d32f2f;">Unable to display PDF</h2>
                  <p>Your browser may not support PDF viewing.</p>
                  <p><a href="${pdfUrl}" download="${this.escapeHtml(fileName)}" 
                        style="color: #1976d2; text-decoration: none; 
                               padding: 10px 20px; border: 2px solid #1976d2; 
                               border-radius: 4px;">Download PDF Instead</a></p>
                </div>
              \`;
            });
          </script>
        </body>
        </html>
      `;

      // Create blob from HTML content
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      const htmlUrl = URL.createObjectURL(htmlBlob);
      
      // Open in new window/tab
      const newWindow = window.open(htmlUrl, '_blank');
      
      // Clean up HTML blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(htmlUrl);
      }, 1000);
      
    } catch (error) {
      console.error('Error opening PDF:', error);
      // Fallback - just open the blob URL directly
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
    }
  }

  /**
   * Helper method to escape HTML characters for safe insertion into HTML strings
   * Prevents XSS attacks when inserting dynamic content
   * 
   * @param text - Text to escape
   * @returns Escaped HTML string safe for insertion
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Downloads a PDF blob with proper filename
   * Creates a temporary download link and triggers the download
   * 
   * @param pdfBlob - PDF blob data to download
   * @param fileName - Filename for the downloaded file
   */
  private downloadPdf(pdfBlob: Blob, fileName: string): void {
    try {
      const url = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      this._toastService.showErrorMessage('Failed to download PDF');
    }
  }

  /**
   * HTTP POST request to generate a PDF with structured request data
   * Sends HTML content and attachment information to the backend for PDF generation
   * 
   * @param request - PDF generation request with HTML content and metadata
   * @returns Observable of PDF generation response with blob and filename
   */
  private generatePdf(request: PdfGenerationRequest): Observable<PdfGenerationResponse> {
    return from(this._entraService.acquireToken()).pipe(
      switchMap((accessToken) => {
        const url = `${this.apiUrl}/V1/generate-pdf`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`
        });

        const requestData = {
          formTitle: request.formTitle,
          htmlContent: request.htmlContent,
          imageBlobNames: request.imageBlobNames || [],
          pdfBlobNames: request.pdfBlobNames || [],
          pdfFileName: request.pdfFileName
        };

        const formData = new FormData();
        formData.append('requestData', JSON.stringify(requestData));

        return this._httpClient.post(url, formData, {
          headers,
          responseType: 'blob',
          observe: 'response'
        }).pipe(
          map(response => {
            const blob = response.body as Blob;
            
            // Extract filename from Content-Disposition header or use fallback
            let filename = request.pdfFileName || 'MacSafety-Form.pdf';
            const contentDisposition = response.headers.get('content-disposition');
            
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
              if (filenameMatch?.[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '');
              }
            }

            return { blob, filename };
          })
        );
      })
    );
  }

  /**
   * Clears all PDF cache entries from localStorage
   * Useful for freeing up storage space or clearing corrupted cache
   */
  public clearCache(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} PDF cache entries`);
  }

  /**
   * Gets cache statistics for monitoring and debugging
   * Returns count and total size of cached PDFs
   * 
   * @returns Object containing cache count, total size in bytes, and total size in MB
   */
  public getCacheStats(): {count: number, totalSize: number, totalSizeMB: number} {
    let count = 0;
    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const data: CachedPdf = JSON.parse(cached);
            count++;
            totalSize += data.size;
          }
        } catch {
          // Skip corrupted entries
        }
      }
    }

    return { 
      count, 
      totalSize,
      totalSizeMB: Math.round((totalSize / (1024 * 1024)) * 100) / 100
    };
  }

  /**
   * Removes a specific PDF from cache by form entry ID
   * Useful for clearing individual cache entries when needed
   * 
   * @param formEntryId - Unique identifier of the form entry to remove from cache
   */
  public removeCachedPdf(formEntryId: string): void {
    const cacheKey = this.CACHE_PREFIX + formEntryId;
    localStorage.removeItem(cacheKey);
    console.log(`Removed cached PDF: ${formEntryId}`);
  }
}